// Los links de las referencias de un post — ADR-2026-10-08-el-post-tiene-planificacion-y-referencias.
//
// Las referencias se escriben como texto libre (se pega un link, se anota una cuenta, se
// comenta por qué sirve) y de ahí se sacan los links para mostrarlos clicables. Sin React y
// sin red, para probarlo.

export interface LinkDeReferencia {
  /** Lo que se abre. Siempre con protocolo. */
  url: string;
  /** Cómo se muestra: el dominio sin "www." y, si hay, un pedazo del camino. */
  etiqueta: string;
  /** Una cuenta o un post de Instagram: se le pone su ícono. */
  esInstagram: boolean;
}

// Una URL con protocolo, una que empieza con "www.", o un dominio suelto con camino
// ("instagram.com/marca"). El dominio suelto exige un TLD de letras para no tomar
// "10.30" o una hora como un link.
const URL_RE =
  /\b(?:https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}\/[^\s<>"'`]*)/gi;
// Una cuenta de Instagram: "@marca", no pegada a otra palabra (un mail no es una cuenta).
const CUENTA_RE = /(^|[\s(])@([a-z0-9._]{2,30})\b/gi;

/** Lo que suele quedar pegado al final de un link escrito en una oración. */
function sinPuntuacionFinal(url: string): string {
  let u = url;
  while (/[.,;:!?)\]}'"»]$/.test(u)) {
    // Un ")" que cierra un "(" del propio link (Wikipedia) se queda.
    if (u.endsWith(')') && (u.match(/\(/g)?.length ?? 0) >= (u.match(/\)/g)?.length ?? 0)) break;
    u = u.slice(0, -1);
  }
  return u;
}

function etiquetaDe(url: URL): string {
  const dominio = url.hostname.replace(/^www\./, '');
  const camino = url.pathname.replace(/\/+$/, '');
  if (!camino) return dominio;
  const corto = camino.length > 28 ? `${camino.slice(0, 27)}…` : camino;
  return `${dominio}${corto}`;
}

/** Para comparar: sin "www.", sin la barra final y el dominio en minúscula. */
function claveDe(url: URL): string {
  return `${url.hostname.toLowerCase().replace(/^www\./, '')}${url.pathname.replace(/\/+$/, '')}${url.search}`;
}

/** Los links de un texto, sin repetidos y en el orden en que aparecen. */
export function linksDe(texto: string): LinkDeReferencia[] {
  const vistos = new Set<string>();
  const out: Array<LinkDeReferencia & { pos: number }> = [];

  for (const m of texto.matchAll(URL_RE)) {
    const crudo = sinPuntuacionFinal(m[0]);
    const conProtocolo = /^https?:\/\//i.test(crudo) ? crudo : `https://${crudo}`;
    let url: URL;
    try {
      url = new URL(conProtocolo);
    } catch {
      continue;
    }
    const clave = claveDe(url);
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    out.push({
      url: url.href,
      etiqueta: etiquetaDe(url),
      esInstagram: /(^|\.)instagram\.com$/i.test(url.hostname),
      pos: m.index ?? 0,
    });
  }

  for (const m of texto.matchAll(CUENTA_RE)) {
    const cuenta = m[2]!.replace(/\.+$/, '');
    const url = `https://www.instagram.com/${cuenta}/`;
    const clave = claveDe(new URL(url)).toLowerCase();
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    out.push({ url, etiqueta: `@${cuenta}`, esInstagram: true, pos: (m.index ?? 0) + m[1]!.length });
  }

  return out.sort((a, b) => a.pos - b.pos).map(({ pos: _pos, ...l }) => l);
}
